<script>
  import { router, useForm } from '@inertiajs/svelte'
  import { liveValidate } from '../../lib/live_validation.svelte.js'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import FormLayout from '../../components/form_layout.svelte'
  import FormSection from '../../components/form_section.svelte'
  import { Button } from '../../components/ui'
  import MultiSelect from '../../components/multi_select.svelte'
  import ImageSelector from '../../components/image_selector.svelte'
  import TextField from '../../components/form/text_field.svelte'
  import TextareaField from '../../components/form/textarea_field.svelte'
  import SelectField from '../../components/form/select_field.svelte'
  import CheckboxField from '../../components/form/checkbox_field.svelte'
  import ToggleField from '../../components/form/toggle_field.svelte'
  import TimeField from '../../components/form/time_field.svelte'
  import NumberField from '../../components/form/number_field.svelte'
  import SubmitButton from '../../components/form/submit_button.svelte'
  import QuickCreatePlaceDrawer from '../../components/places/quick_create_place_drawer.svelte'

  // Options arrive labelled and filtered; the per-type field rules (clearing the other kind's
  // fields, forcing a store item's policy, nulling a non-reward point source, gallery
  // positions) run in AdventuresController#adventure_attributes on submit.
  let {
    adventure,
    options = {},
    back_url: backUrl = '/adventures'
  } = $props()

  const isEdit = !!adventure.id
  const url = isEdit ? `/adventures/${adventure.id}` : '/adventures'

  // Nested under `adventure` with dotted names, no transform: Precognition's
  // validate('adventure.title') reads the value at that path. Every change is validated live.
  const form = useForm({
    adventure: { ...structuredClone(adventure.fields), image_ids: adventure.images.map((image) => image.id) },
    return_to: backUrl
  }).withPrecognition(isEdit ? 'patch' : 'post', url)
  liveValidate(form, 'adventure')

  let images = $state([...adventure.images])
  $effect(() => {
    $form.adventure.image_ids = images.map((image) => image.id)
  })
  let newPlaceOpen = $state(false)
  const error = (key) => $form.errors[`adventure.${key}`]

  const isStoreItem = $derived($form.adventure.adventure_type === 'store_item')

  // Type, town and place changes recalculate the description / categories / policy / place
  // defaults on the server (AdventuresController#form_defaults); the answer lands in the form.
  const refreshDefaults = () => {
    const { adventure_type, description, place_id, sub_category_ids, redemption_policy, area_id } = $form.adventure
    router.reload({
      only: ['form_defaults'],
      data: { defaults: { adventure_type, description, place_id, sub_category_ids: sub_category_ids.join(','), redemption_policy, area_id } },
      preserveUrl: true,
      preserveState: true,
      preserveScroll: true,
      onSuccess: (page) => {
        const defaults = page.props.form_defaults
        if (!defaults) return
        $form.adventure.description = defaults.description
        $form.adventure.sub_category_ids = defaults.sub_category_ids
        $form.adventure.redemption_policy = defaults.redemption_policy
        $form.adventure.place_id = defaults.place_id
      }
    })
  }

  let lastType = $form.adventure.adventure_type
  $effect(() => {
    const type = $form.adventure.adventure_type
    if (type === lastType) return
    lastType = type
    refreshDefaults()
  })

  const onPlaceCreated = (createdPlace) => {
    if (createdPlace.area_id) $form.adventure.area_id = createdPlace.area_id
    $form.adventure.place_id = createdPlace.id
    refreshDefaults()
  }

  const submit = () => {
    if (isEdit) $form.patch(url)
    else $form.post(url)
  }
</script>

<Layout>
  <PageHeader title={isEdit ? 'Edit Adventure' : 'New Adventure'} subtitle="Rewards, store items, and events members can redeem" />

  <FormLayout
    onsubmit={(event) => {
      event.preventDefault()
      submit()
    }}
  >
    <FormSection title="Adventure Info">
      <TextField
        bind:value={$form.adventure.name}
        label="Name"
        name="name"
        help="This value is only for internal use and is not displayed in the app."
        error={error('name')}
      />
      <SelectField bind:value={$form.adventure.adventure_type} label="Type" name="adventure_type" options={options.adventure_types} error={error('adventure_type')} />
      <SelectField
        bind:value={$form.adventure.area_id}
        label="College Town"
        name="area_id"
        options={options.areas}
        onchange={refreshDefaults}
        error={error('area_id')}
      />
      <SelectField
        bind:value={$form.adventure.place_id}
        label="Place"
        name="place_id"
        source="places"
        sourceParams={{ area_id: $form.adventure.area_id }}
        onchange={refreshDefaults}
        error={error('place_id')}
      />
      <div class="-mt-2">
        <Button variant="link" size="xs" icon="add" onclick={() => (newPlaceOpen = true)}>New place</Button>
      </div>

      {#if isStoreItem}
        <CheckboxField
          bind:checked={$form.adventure.store_item_active}
          label="Active"
          name="store_item_active"
          help="Use this to temporarily hide the store item from being purchased with points. It does not disable redemption for those who have already purchased."
        />
      {/if}

      {#if $form.adventure.adventure_type === 'reward'}
        <SelectField
          bind:value={$form.adventure.point_source_id}
          label="Point Source"
          name="point_source_id"
          options={options.point_sources}
          placeholder="Select a point source…"
          error={error('point_source_id')}
        />
      {/if}

      {#if isStoreItem}
        <SelectField
          bind:value={$form.adventure.tremendous_product_id}
          label="Tremendous Product"
          name="tremendous_product_id"
          source="tremendous_products"
          error={error('tremendous_product_id')}
        />
        <TextField bind:value={$form.adventure.tremendous_value} label="Tremendous Value" name="tremendous_value" placeholder="$25" error={error('tremendous_value')} />
      {/if}

      <MultiSelect bind:value={$form.adventure.sub_category_ids} options={options.sub_categories} label="Categories" placeholder="Select a category…" />
    </FormSection>

    <FormSection title={isStoreItem ? 'Store Item Details' : 'Default Details'}>
      {#if !isStoreItem}
        <TextField bind:value={$form.adventure.title} label="Title" name="title" error={error('title')} />
        <TextareaField bind:value={$form.adventure.description} label="Description" name="description" error={error('description')} />
      {/if}

      {#if isStoreItem}
        <TextField bind:value={$form.adventure.pre_purchase_title} label="Pre-Claim Title" name="pre_purchase_title" error={error('pre_purchase_title')} />
        <TextField bind:value={$form.adventure.post_purchase_title} label="Post-Claim Title" name="post_purchase_title" error={error('post_purchase_title')} />
        <TextareaField bind:value={$form.adventure.pre_purchase_description} label="Pre-Claim Description" name="pre_purchase_description" error={error('pre_purchase_description')} />
        <TextareaField bind:value={$form.adventure.post_purchase_description} label="Post-Claim Description" name="post_purchase_description" error={error('post_purchase_description')} />
        <NumberField bind:value={$form.adventure.point_cost} label="Point Cost" name="point_cost" error={error('point_cost')} />
      {/if}

      <TextField bind:value={$form.adventure.savings} label="Estimated Savings" name="savings" placeholder="$10" error={error('savings')} />
      <TextField bind:value={$form.adventure.spend} label="Estimated Spend" name="spend" placeholder="$20" error={error('spend')} />
      <TextField bind:value={$form.adventure.zupp_spend} label="Estimated Zupp Spend" name="zupp_spend" placeholder="$5" error={error('zupp_spend')} />

      {#if isStoreItem}
        <NumberField bind:value={$form.adventure.cooldown_period_days} label="Cooldown Period (Days)" name="cooldown_period_days" error={error('cooldown_period_days')} />
      {:else}
        <NumberField bind:value={$form.adventure.daily_claim_limit} label="Daily Claim Limit" name="daily_claim_limit" error={error('daily_claim_limit')} />
      {/if}

      {#if isStoreItem}
        <!-- Store items are always once-per-purchase; the server sets it on save. -->
        <SelectField value="once_per_purchase" label="Redemption Policy" name="redemption_policy" options={options.store_item_redemption_policies} disabled />
      {:else}
        <SelectField
          bind:value={$form.adventure.redemption_policy}
          label="Redemption Policy"
          name="redemption_policy"
          options={options.redemption_policies}
          error={error('redemption_policy')}
        />
      {/if}

      {#if $form.adventure.redemption_policy === 'once_per_period' && !isStoreItem}
        <NumberField bind:value={$form.adventure.once_per_period_days} label="Redemption Period Days" name="once_per_period_days" error={error('once_per_period_days')} />
      {/if}

      <CheckboxField bind:checked={$form.adventure.requires_location} label="Require Geofence Location" name="requires_location" error={error('requires_location')} />

      {#if !isStoreItem}
        <NumberField bind:value={$form.adventure.group_required_count} label="Minimum Group Members" name="group_required_count" error={error('group_required_count')} />
      {/if}

      {#if isStoreItem}
        <SelectField bind:value={$form.adventure.multiplier} label="Multiplier Type" name="multiplier" options={options.multiplier_options} error={error('multiplier')} />
        <NumberField bind:value={$form.adventure.multiplier_days} label="Multiplier Days" name="multiplier_days" error={error('multiplier_days')} />
      {/if}
    </FormSection>

    {#if !isStoreItem}
      <FormSection title="Scheduling">
        <TimeField bind:value={$form.adventure.starts_at} label="Starts At" name="starts_at" error={error('starts_at')} />
        <TimeField bind:value={$form.adventure.ends_at} label="Ends At" name="ends_at" error={error('ends_at')} />
      </FormSection>
    {/if}

    <FormSection title="Images">
      <ImageSelector bind:value={images} label="Image(s)" placeId={$form.adventure.place_id} />
    </FormSection>

    {#if error('base')}<p class="text-sm text-error">{error('base')}</p>{/if}

    {#snippet footer()}
      <Button href={backUrl} variant="ghost" size="sm">Cancel</Button>
      <SubmitButton processing={$form.processing} full={false} />
    {/snippet}
  </FormLayout>

  <QuickCreatePlaceDrawer bind:open={newPlaceOpen} areaOptions={options.areas} subCategoryOptions={options.sub_categories} areaId={$form.adventure.area_id} oncreated={onPlaceCreated} />
</Layout>
